/**
 * "Not a fit, in the agent's view", grouped by WHY (Sofia, Oct 23 2026). One group per contradicted
 * must-have, plus "works or worked at FINN" and "already contacted for this role". Someone with
 * several contradicted must-haves sits under the FIRST one (the card names the rest), so the group
 * sizes add up to the list. Rows saved before `sourcing.skip` existed are read from the reason text.
 */
import type { OutreachCandidate, SourcingSkip } from '../../types/taOutreach';

export interface MisfitGroup {
  key: string;
  title: string;
  people: OutreachCandidate[];
}

const FORMER_FINN = 'Works or worked at FINN';

/** The reason as data: stored since Oct 23 2026, else derived from the text. */
export function skipOf(c: OutreachCandidate): SourcingSkip | null {
  const s = c.sourcing;
  if (!s?.skipReason) return null;
  if (s.skip) return s.skip;
  const text = s.skipReason;
  if (text.startsWith(FORMER_FINN)) return { kind: 'former_finn', criteria: [], source: 'list' };
  if (s.skipSource === 'role_contact' || /^Already contacted for this role/i.test(text)) return { kind: 'role_contact', criteria: [], source: 'role_contact' };
  const full = text.startsWith('Full profile contradicts: ');
  if (!full && !text.startsWith('Card contradicts: ')) return null;
  // The text lists labels with quotes and separators that labels can contain themselves (commas):
  // the candidate's own criteria found in it, in the order they appear, are the reliable read.
  const labels = [...(s.evidence || []).map((e) => e.label), ...(c.gap?.criteria || []).map((x) => x.label)];
  const lower = text.toLowerCase();
  const found = [...new Set(labels)]
    .map((l) => ({ l, at: lower.indexOf(l.toLowerCase()) }))
    .filter((x) => x.at >= 0)
    .sort((a, b) => a.at - b.at)
    .map((x) => x.l);
  return { kind: 'must_have', criteria: found, source: full ? 'full_profile' : 'list' };
}

export function misfitGroups(misfits: OutreachCandidate[]): MisfitGroup[] {
  const groups = new Map<string, MisfitGroup>();
  const add = (key: string, title: string, c: OutreachCandidate) => {
    if (!groups.has(key)) groups.set(key, { key, title, people: [] });
    groups.get(key)!.people.push(c);
  };
  for (const c of misfits) {
    const skip = skipOf(c);
    if (skip?.kind === 'former_finn') add('former_finn', FORMER_FINN, c);
    else if (skip?.kind === 'role_contact') add('role_contact', 'Already contacted for this role', c);
    else if (skip?.criteria[0]) add(`must:${skip.criteria[0]}`, `Contradicts: ${skip.criteria[0]}`, c);
    else add('other', 'Other reason', c);
  }
  // Biggest first; "Other reason" always last.
  return [...groups.values()].sort((a, b) => Number(a.key === 'other') - Number(b.key === 'other') || b.people.length - a.people.length);
}
